'use client';

import { useEffect } from 'react';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';
import { cn } from '@/utils';
import { ToastConfig } from '@/types';

/**
 * 토스트 컴포넌트 Props
 */
interface ToastProps extends ToastConfig {
  onClose: () => void;
}

/**
 * 토스트 알림 컴포넌트
 * - 다양한 타입 지원 (success, error, warning, info)
 * - 자동 사라짐 기능
 * - 애니메이션 효과
 */
export function Toast({
  type,
  title,
  message,
  duration = 5000,
  position = 'top-right',
  onClose,
}: ToastProps) {
  // 자동 사라짐
  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(onClose, duration);
      return () => clearTimeout(timer);
    }
  }, [duration, onClose]);

  const icons = {
    success: CheckCircle,
    error: XCircle,
    warning: AlertTriangle,
    info: Info,
  };

  const colors = {
    success: 'bg-green-50 border-green-200 text-green-800',
    error: 'bg-red-50 border-red-200 text-red-800',
    warning: 'bg-yellow-50 border-yellow-200 text-yellow-800',
    info: 'bg-blue-50 border-blue-200 text-blue-800',
  };

  const iconColors = {
    success: 'text-green-500',
    error: 'text-red-500',
    warning: 'text-yellow-500',
    info: 'text-blue-500',
  };

  const positions = {
    'top': 'top-4 left-1/2 transform -translate-x-1/2',
    'bottom': 'bottom-4 left-1/2 transform -translate-x-1/2',
    'top-left': 'top-4 left-4',
    'top-right': 'top-4 right-4',
    'bottom-left': 'bottom-4 left-4',
    'bottom-right': 'bottom-4 right-4',
  };

  const Icon = icons[type];

  return (
    <div
      className={cn(
        'fixed z-50 max-w-sm w-full p-4 rounded-lg border shadow-lg',
        'animate-in slide-in-from-right-full duration-300',
        colors[type],
        positions[position]
      )}
    >
      <div className="flex items-start gap-3">
        <Icon size={20} className={cn('flex-shrink-0 mt-0.5', iconColors[type])} />
        
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm">{title}</p>
          {message && (
            <p className="text-sm opacity-90 mt-1">{message}</p>
          )}
        </div>

        <button
          onClick={onClose}
          className="flex-shrink-0 p-1 rounded-md hover:bg-black hover:bg-opacity-10 transition-colors"
          aria-label="닫기"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}

/**
 * 토스트 컨테이너 컴포넌트
 * - 여러 토스트를 관리
 * - 위치별 그룹화
 */
interface ToastContainerProps {
  toasts: Array<ToastConfig & { id: string }>;
  onRemove: (id: string) => void;
}

export function ToastContainer({ toasts, onRemove }: ToastContainerProps) {
  return (
    <div className="fixed inset-0 pointer-events-none z-50">
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          {...toast}
          onClose={() => onRemove(toast.id)}
        />
      ))}
    </div>
  );
}
